import { ICategory, ICategoryBoard, IGrades, ISubject } from '@wot/typings';
import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import categoryApi, { CategoryReqBody } from '../../../api/categories';
import { MainBody } from '../../../components/MainBody';
import { Button, MultiSelectDropDown, TextInput } from '@wot/components';
import BreadCrumb from '../../../components/BreadCrumb';

const gradeOptions: IGrades[] = [
  'Grade I',
  'Grade II',
  'Grade III',
  'Grade IV',
  'Grade V',
  'Grade VI',
  'Grade VII',
  'Grade VIII',
  'Grade IX',
  'Grade X',
  'Grade XI',
  'Grade XI',
  'College Level'
];
const boardOptions: ICategoryBoard[] = [
  'ICSE',
  'CBSE',
  'State Board',
  'Common Core State Standards (CCSS)',
  'Next Generation Science Standards (NGSS)',
  'Advanced Placement (AP) Program',
  'International Baccalaureate (IB)',
  'State-specific Curriculums',
  'National Curriculum (England)',
  'Cambridge Assessment International Education (CAIE)',
  'Edexcel (Pearson) Curriculum',
  'OCR Curriculum',
  'Australian Curriculum (ACARA)',
  'Provincial Curriculums',
  'New Zealand Curriculum (NZC)',
  'National Certificate of Educational Achievement (NCEA)',
  'Singapore National Curriculum',
  'GCE O-Level / N-Level / A-Level',
  'Cambridge Primary & Secondary',
  'UAE National Curriculum',
  'British Curriculum',
  'American Curriculum',
  'Indian Curriculum',
  'Competency-Based Curriculum (CBC)',
  '8-4-4 System'
];

export function AddSubject() {
  const navigate = useNavigate();
  const [tempSubjects, setTempSubjects] = useState<ISubject[]>([]);
  const [error, setError] = useState(false);
  const [, setLoading] = useState(false);
  const [categories, setCategories] = useState<ICategory<ISubject>[]>([]);

  useEffect(() => {
    categoryApi
      .getCategories('subject')
      .then((data) => {
        if (data) {
          setCategories(data);
        }
      })
      .catch((err) => err);
  }, []);

  const checkIfExists = (temp: ISubject[], categories: ICategory<ISubject>[], newValue: ISubject) => {
    let exists = temp.filter((s) => s.name == newValue.name).length > 0;
    if (exists) {
      toast.error(`${newValue.name} Subject already added`);
      return true;
    }
    let existsInDB = categories.filter((s) => s.category.name == newValue.name).length > 0;
    if (existsInDB) {
      toast.error(`${newValue.name} Subject already exists.`);
      return true;
    }
  };

  const handleFormChange = (newValues: ISubject, index?: number, remove?: number) => {
    let temp = [...tempSubjects];
    if (index) {
      if (remove) {
        temp = temp.filter((item, idx) => idx !== index - 1);
        setTempSubjects(temp);
        return;
      }
      let stemp = temp.filter((_, i) => i !== index - 1);
      const exists = checkIfExists(stemp, categories, newValues);
      if (exists) {
        return;
      }
      temp[index - 1] = newValues;
      setTempSubjects(temp);
    } else {
      const exists = checkIfExists(temp, categories, newValues);
      if (exists) {
        return;
      }
      temp.push(newValues);
      setTempSubjects(temp);
    }
  };

  useEffect(() => {
    let error = tempSubjects.some((item) => !item.name || item.grades.length === 0 || item.board.length === 0, []);
    if (error) {
      setError(error);
      return;
    } else {
      setError(false);
    }
  }, [tempSubjects]);

  const addSubjects = () => {
    setLoading(true);
    let subjects: CategoryReqBody[] = [];
    tempSubjects.forEach((x) => {
      subjects.push({
        type: 'subject',
        category: x
      });
    });
    categoryApi
      .addCategories(subjects)
      .then((data) => {
        if (data) {
          navigate(-1);
          toast.success('Subject added successfully!');
        } else {
          toast.error('Something went wrong!');
        }
        setLoading(false);
      })
      .catch(() => {
        toast.error('Something went wrong');
      });
  };

  return (
    <MainBody>
      <div className="relative w-full flex flex-col p-4 px-6 bg-white">
        <BreadCrumb />
        <CurrentSubjectForm handleFormChange={handleFormChange} />
        <div className="w-full h-[2px] bg-neutralGray1 my-10"></div>
        <div className="flex flex-col gap-5">
          {tempSubjects.map((subject, index) => (
            <SubjectItem subject={subject} handleFormChange={handleFormChange} index={index + 1} key={index} />
          ))}
        </div>

        {tempSubjects.length > 0 && (
          <div className="w-full bg-white py-5">
            <div className="max-w-sm w-full mx-auto flex gap-4 items-center justify-center">
              <Button text={'Cancel'} type="s-outlined" onClick={() => navigate('/general-management/categories')} />
              <Button disabled={error} text={'Save'} onClick={() => addSubjects()} />
            </div>
          </div>
        )}
      </div>
    </MainBody>
  );
}

export interface SubjectFormError {
  name?: boolean;
  grades?: boolean;
  boards?: boolean;
  strengths?: boolean;
  experience?: boolean;
}

interface FormProps {
  subject: ISubject;
  handleFormChange: (v: ISubject, index?: number, remove?: number) => void;
  index?: number;
}

function CurrentSubjectForm({ handleFormChange }: { handleFormChange: (v: ISubject, index?: number) => void }) {
  const [currentSubject, setCurrentSubject] = useState<ISubject>({
    name: '',
    grades: [],
    board: []
  });
  const [errors, setErrors] = useState<SubjectFormError>();

  return (
    <div className="w-full flex flex-col my-5 gap-7">
      <div className="w-full flex items-center justify-between">
        <h1 className="text-xl font-semibold">Subject</h1>
        <div className="max-w-xs">
          <Button
            text="+ Add New Subject"
            onClick={() => {
              if (!currentSubject.name || !currentSubject.grades.length || !currentSubject.board.length) {
                setErrors({
                  ...errors,
                  name: !currentSubject.name,
                  grades: currentSubject.grades.length === 0,
                  boards: currentSubject.board.length === 0
                });
                return;
              }
              setErrors(undefined);
              handleFormChange(currentSubject);
              setCurrentSubject({
                name: '',
                grades: [],
                board: []
              });
            }}
          />
        </div>
      </div>

      <SubjectForm
        name={currentSubject.name}
        setName={(value) => setCurrentSubject({ ...currentSubject, name: value })}
        boardsList={currentSubject.board}
        gradesList={currentSubject.grades}
        setGradesList={(list) => setCurrentSubject({ ...currentSubject, grades: list })}
        setBoardsList={(list) => setCurrentSubject({ ...currentSubject, board: list })}
        errors={errors}
      />
    </div>
  );
}

function SubjectItem({ subject, handleFormChange, index }: FormProps) {
  const [errors, setErrors] = useState<SubjectFormError>();

  useEffect(() => {
    if (!subject.name || !subject.grades.length || !subject.board.length) {
      setErrors({
        ...errors,
        name: !subject.name,
        grades: subject.grades.length === 0,
        boards: subject.board.length === 0
      });
      return;
    } else {
      setErrors(undefined);
    }
  }, [subject]);
  return (
    <div className="w-full flex items-start gap-10 bg-white border-b">
      <SubjectForm
        name={subject.name}
        setName={(value) => handleFormChange({ ...subject, name: value }, index)}
        gradesList={subject.grades}
        setGradesList={(list) => handleFormChange({ ...subject, grades: list }, index)}
        boardsList={subject.board}
        setBoardsList={(list) => handleFormChange({ ...subject, board: list }, index)}
        errors={errors}
      />
      <MdClose className="w-4 cursor-pointer" onClick={() => handleFormChange(subject, index, 1)} />
    </div>
  );
}

interface SubjectFormProps {
  name: string;
  setName: (value: string) => void;
  gradesList: IGrades[];
  setGradesList: (grades: IGrades[]) => void;
  boardsList: ICategoryBoard[];
  setBoardsList: (boards: ICategoryBoard[]) => void;
  errors?: SubjectFormError;
}

export function SubjectForm({
  name,
  setName,
  gradesList,
  setGradesList,
  boardsList,
  setBoardsList,
  errors
}: SubjectFormProps) {
  return (
    <div className="w-full max-w-lg flex flex-col my-5 gap-7">
      <div className="flex items-center justify-between">
        <h3 className="w-1/3 text-sm shrink-0">Subject Name</h3>
        <div className="w-full max-w-md flex flex-col">
          <TextInput value={name} onChange={(e: any) => setName(e)} placeholder="Add Subject" type="text" />
          {errors && errors?.name && <p className="text-xs font-medium text-red-400">Please Add Subject Name</p>}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="w-1/3 text-sm shrink-0">Grades</h3>
        <div className="w-full h-full flex flex-col">
          <MultiSelectDropDown
            placeholder="Add Grades"
            onChange={(values: any) => setGradesList(values)}
            values={gradesList}
            options={gradeOptions}
            selectAll
          />
          {errors && errors?.grades && (
            <p className="text-xs font-medium text-red-400">Please Select one of the Grades</p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="w-1/3 text-sm shrink-0">Board</h3>
        <div className="w-full h-full flex flex-col">
          <MultiSelectDropDown
            placeholder="Add Boards"
            onChange={(values: any) => setBoardsList(values)}
            values={boardsList}
            options={boardOptions}
            selectAll
          />
          {errors && errors?.boards && (
            <p className="text-xs font-medium text-red-400">Please Select one of the Boards</p>
          )}
        </div>
      </div>
    </div>
  );
}
